/**
 * Implements infinite scrolling / "Show more" for a web component.
 * Top Performance 2026-10-04: clicks are now handled by delegation and elements are
 * re-read on every use, so the button keeps working after Convermax swaps the
 * product grid (e.g. when a subcategory filter is ticked).
 */
if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.delegatedClick = this.onDelegatedClick.bind(this);
      this.addEventListener('click', this.delegatedClick);
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    refreshElements() {
      this.paginationContainer = this.querySelector('.js-pagination');
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
      this.nextButton = this.querySelector('.js-pagination__next-button');
    }

    init() {
      this.refreshElements();
      if (!this.paginationContainer || !this.paginationItems.length || !this.nextButton || !this.nextButton.hasAttribute('href')) {
        return;
      }
      this.bindObserver();
    }

    destroy() {
      clearInterval(this.nextButtonTimer);
      this.intersectionObserver?.disconnect();
    }

    // Any click on the current "Show more" button, even one inserted after page load
    onDelegatedClick(event) {
      const button = event.target.closest('.js-pagination__next-button');
      if (!button || !this.contains(button) || !button.hasAttribute('href')) return;
      event.preventDefault();
      this.fetchNextItems();
    }

    bindObserver() {
      if (this.dataset.infiniteScrollEnabled !== 'true') return;
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.fetchNextItems();
            clearInterval(this.nextButtonTimer);
            this.nextButtonTimer = setInterval(this.fetchNextItems.bind(this), 1500);
          } else {
            clearInterval(this.nextButtonTimer);
          }
        });
      }, {rootMargin: `${window.innerHeight}px 0px`});
      this.intersectionObserver.observe(this.paginationContainer);
    }

    async fetchNextItems() {
      if (this.fetchInProgress) return;
      this.refreshElements();
      if (!this.nextButton || !this.nextButton.hasAttribute('href') || !this.paginationItems.length) return;

      const button = this.nextButton;
      const originalButtonText = button.textContent;
      this.fetchInProgress = true;
      button.setAttribute('aria-disabled', 'true');
      button.textContent = window.strings.general.pagination.loadingText;

      try {
        const response = await fetch(button.href);
        if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);

        const template = document.createElement('template');
        template.innerHTML = await response.text();

        const newItemsHTML = Array.from(template.content.querySelectorAll('.js-pagination__item'))
          .map(item => item.outerHTML)
          .join('');
        this.paginationItems[this.paginationItems.length - 1].insertAdjacentHTML('afterend', newItemsHTML);
        const lastIndex = this.paginationItems.length;
        this.paginationItems = this.querySelectorAll('.js-pagination__item');

        setTimeout(() => {
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
          window.renderSelects?.();
          window.loadDesktopOnlyTemplates?.();
          window.stickersReinit?.();

          if (document.body.classList.contains('tab-active') && window.theme?.settings.paginationLoadType === 'show_more') {
            this.paginationItems[lastIndex]?.querySelector('a')?.focus();
          }
        }, 300);

        this.updatePaginationControls(template);
      } catch (error) {
        console.error('Failed to load new items:', error);
      } finally {
        this.fetchInProgress = false;
        if (button.isConnected) {
          button.removeAttribute('aria-disabled');
          button.textContent = originalButtonText;
        }
      }
    }

    updatePaginationControls(template) {
      const nextPagination = template.content.querySelector('.js-pagination');
      if (nextPagination && nextPagination.querySelector('.js-pagination__next-button[href]')) {
        this.paginationContainer.innerHTML = nextPagination.innerHTML;
        this.refreshElements();
      } else {
        this.cleanupAfterLastPage();
      }
    }

    cleanupAfterLastPage() {
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      clearInterval(this.nextButtonTimer);
      this.paginationContainer?.remove();
      document.querySelectorAll('.js-when-paginated-only').forEach(elem => elem.remove());
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}
